import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle, 
  FileUp, 
  FileDown,
  Info
} from 'lucide-react';

const BulkActionsPage = ({ user }) => {
  const [importProgress, setImportProgress] = useState(0);
  const [exportProgress, setExportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState('append'); // 'append' or 'update'
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewData(null);
    setValidationErrors([]);
    setImportStatus(null);
    setImportResult(null);

    try {
      // Read file content
      const fileContent = await readFile(file);
      
      // Parse CSV
      const questions = parseCSV(fileContent);
      
      if (questions.length === 0) {
        setValidationErrors(['No valid questions found in file']);
        return;
      }

      // Validate data
      const errors = validateQuestions(questions);
      setValidationErrors(errors);
      
      // Set preview data
      setPreviewData({
        questions,
        totalRows: questions.length,
        validRows: questions.length - errors.length
      });

    } catch (error) {
      console.error('File processing error:', error);
      setValidationErrors([`Error processing file: ${error.message}`]);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Create the CSV template content
      const templateContent = `title,description,category,priority,status,module_type,module_reference_id,module_reference_name,project_id,company_id
"Sample Question 1","This is a sample question description","General","Normal","open","","","","",""
"Sample Question 2","Another example with priority","Technical","Priority","open","","","","",""
"Dataroom Question","Question related to dataroom","Dataroom","Normal","open","dataroom","","","123",""
"Equity Question","Question about equity","Equity","Priority","open","equity_manager","","","","456"`;

      // Create and download the file
      const blob = new Blob([templateContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'questions-import-template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData || !selectedFile) return;

    setImportProgress(0);
    setImportStatus('importing');

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 20, 90));
      }, 200);

      // Call import API (using simpleQuestionsApi for now)
      const response = await import('../services/simpleQuestionsApi').then(api => 
        api.default.bulkImportQuestions({
          questions: previewData.questions,
          mode: importMode,
          user_id: user?.id || '1'
        })
      );

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.success) {
        setImportStatus('success');
        setImportResult({
          imported_count: response.imported_count || previewData.validRows,
          total_rows: previewData.totalRows,
          skipped_count: response.skipped_count || 0,
          errors: response.errors || []
        });
      } else {
        setImportStatus('error');
        setImportResult({ message: response.message || 'Import failed' });
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportStatus('error');
      setImportResult({ message: error.message || 'Import failed' });
    }
  };

  // Validation function
  const validateQuestions = (questions) => {
    const errors = [];
    const validStatuses = ['open', 'answered', 'duplicate', 'out_of_scope', 'closed'];
    const validPriorities = ['high', 'medium', 'low', 'normal', 'Priority', 'Normal'];
    const validCategories = ['general', 'technical', 'financial', 'legal', 'billing'];
    const validModules = ['dataroom', 'capraise', 'equity_manager', 'general', ''];

    questions.forEach((question, index) => {
      const rowNum = index + 2; // +2 because CSV is 1-indexed and we skip header

      // Required fields
      if (!question.title || question.title.trim() === '') {
        errors.push(`Row ${rowNum}: Title is required`);
      }
      if (!question.category || question.category.trim() === '') {
        errors.push(`Row ${rowNum}: Category is required`);
      }

      // Validate enums
      if (question.status && !validStatuses.includes(question.status)) {
        errors.push(`Row ${rowNum}: Invalid status "${question.status}". Valid values: ${validStatuses.join(', ')}`);
      }
      if (question.priority && !validPriorities.includes(question.priority)) {
        errors.push(`Row ${rowNum}: Invalid priority "${question.priority}". Valid values: ${validPriorities.join(', ')}`);
      }
      if (question.category && !validCategories.includes(question.category.toLowerCase())) {
        errors.push(`Row ${rowNum}: Invalid category "${question.category}". Valid values: ${validCategories.join(', ')}`);
      }
      if (question.module_type && !validModules.includes(question.module_type)) {
        errors.push(`Row ${rowNum}: Invalid module_type "${question.module_type}". Valid values: ${validModules.join(', ')}`);
      }

      // Validate numeric fields
      if (question.module_reference_id && isNaN(parseInt(question.module_reference_id))) {
        errors.push(`Row ${rowNum}: module_reference_id must be a number`);
      }
      if (question.project_id && isNaN(parseInt(question.project_id))) {
        errors.push(`Row ${rowNum}: project_id must be a number`);
      }
      if (question.company_id && isNaN(parseInt(question.company_id))) {
        errors.push(`Row ${rowNum}: company_id must be a number`);
      }
    });

    return errors;
  };

  const handleExportQuestions = async (format = 'csv') => {
    setExportProgress(0);
    setExportStatus('exporting');

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 20, 90));
      }, 200);

      // Get questions from simpleQuestionsApi
      const questionsApi = await import('../services/simpleQuestionsApi');
      const response = await questionsApi.default.getQuestions({});

      clearInterval(progressInterval);
      setExportProgress(100);

      if (response.success && response.data) {
        const questions = response.data;
        
        if (format === 'csv') {
          // Generate CSV
          const csvContent = generateCSV(questions);
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `questions_export_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          // For JSON
          const blob = new Blob([JSON.stringify(questions, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `questions_export_${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      } else {
        throw new Error('Failed to fetch questions for export');
      }

      setExportStatus('success');
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus('error');
      setExportProgress(0);
    }
  };

  // Helper function to read file
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  // Helper function to parse CSV
  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length < 2) return []; // Need at least header + 1 row

    const headers = lines[0].split(',').map(h => h.trim());
    const questions = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) continue; // Skip malformed rows

      const question = {};
      headers.forEach((header, index) => {
        question[header] = values[index].trim();
      });

      // Only add if has required fields
      if (question.title && question.category) {
        questions.push(question);
      }
    }

    return questions;
  };

  // Helper to parse CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    
    return result;
  };

  // Helper function to generate CSV
  const generateCSV = (questions) => {
    const headers = [
      'id', 'title', 'description', 'category', 'priority', 'status', 
      'module_type', 'module_reference_id', 'module_reference_name', 
      'project_id', 'company_id', 'user_name', 'created_at', 'updated_at'
    ];
    
    const csvRows = [headers.join(',')];
    
    questions.forEach(question => {
      const row = headers.map(header => {
        const value = question[header] || '';
        // Escape quotes and wrap in quotes if contains comma
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvRows.push(row.join(','));
    });
    
    return csvRows.join('\n');
  };

  const getStatusAlert = (status, progress, type, result) => {
    if (status === 'importing' || status === 'exporting') {
      return (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm text-blue-700 mb-1">
                <span>{type === 'import' ? 'Importing questions...' : 'Exporting questions...'}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (status === 'success') {
      return (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <div className="flex-1">
              <span className="text-sm text-green-700 font-medium">
                {type === 'import' ? 'Import completed!' : 'Export completed!'}
              </span>
              {type === 'import' && result && (
                <div className="mt-2 text-sm text-green-700">
                  <p>Imported: {result.imported_count} questions</p>
                  {result.skipped_count > 0 && <p>Skipped: {result.skipped_count} rows</p>}
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Errors:</p>
                      <ul className="list-disc list-inside">
                        {result.errors.slice(0, 5).map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                        {result.errors.length > 5 && <li>...and {result.errors.length - 5} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (status === 'error') {
      return (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <div className="flex-1">
              <span className="text-sm text-red-700 font-medium">
                {type === 'import' ? 'Import failed' : 'Export failed'}
              </span>
              {result && result.message && (
                <p className="mt-1 text-sm text-red-600">{result.message}</p>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        Bulk Actions
      </h1>
      <p className="text-gray-600 mb-8">
        Import, export, and perform bulk operations on questions
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Import Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Questions</h2>
          <p className="text-gray-600 mb-4">Import questions from CSV files with validation and preview</p>
          
          {/* Mode Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Import Mode:</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="importMode"
                  value="append"
                  checked={importMode === 'append'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Append (Create new questions)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="importMode"
                  value="update"
                  checked={importMode === 'update'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Update (Update existing by ID)</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {importMode === 'append' 
                ? 'New questions will be created. Leave ID field empty.' 
                : 'Existing questions will be updated. Include ID field for updates.'
              }
            </p>
          </div>

          <div className="flex gap-3 mb-4">
            <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
              <Upload className="h-4 w-4 mr-2" />
              Choose File
              <input
                type="file"
                className="hidden"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={importStatus === 'importing'}
              />
            </label>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </button>
          </div>

          {selectedFile && (
            <div className="mb-4 text-sm text-gray-600">
              Selected file: <span className="font-medium">{selectedFile.name}</span>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h3>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.slice(0, 5).map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
                {validationErrors.length > 5 && (
                  <li>• ...and {validationErrors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          )}

          {/* Preview Data */}
          {previewData && validationErrors.length === 0 && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-sm font-medium text-green-800 mb-2">Preview ({previewData.totalRows} questions):</h3>
              <div className="text-sm text-green-700 mb-3">
                <p>✓ {previewData.validRows} valid questions ready to import</p>
                <p>Mode: {importMode === 'append' ? 'Append (Create new)' : 'Update (Modify existing)'}</p>
              </div>
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-green-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Title</th>
                      <th className="px-2 py-1 text-left">Category</th>
                      <th className="px-2 py-1 text-left">Priority</th>
                      <th className="px-2 py-1 text-left">Module</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.questions.slice(0, 5).map((question, index) => (
                      <tr key={index} className="border-t border-green-200">
                        <td className="px-2 py-1 truncate max-w-xs">{question.title}</td>
                        <td className="px-2 py-1">{question.category}</td>
                        <td className="px-2 py-1">{question.priority || 'Normal'}</td>
                        <td className="px-2 py-1">{question.module_type || 'General'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.questions.length > 5 && (
                  <p className="text-xs text-green-600 mt-2">...and {previewData.questions.length - 5} more</p>
                )}
              </div>
              <button
                onClick={handleConfirmImport}
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Confirm Import
              </button>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">CSV Format Requirements:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>title</strong> - Question title (required)
              </li>
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>category</strong> - Question category (required)
              </li>
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>description</strong> - Question description (optional)
              </li>
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>priority</strong> - Priority or Normal (optional)
              </li>
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>status</strong> - open, answered, duplicate, out_of_scope, closed (optional)
              </li>
              <li className="flex items-center">
                <FileUp className="h-4 w-4 mr-2 text-gray-400" />
                <strong>module_type</strong> - dataroom, capraise, equity_manager, general (optional)
              </li>
            </ul>
          </div>

          {getStatusAlert(importStatus, importProgress, 'import', importResult)}
        </div>

        {/* Export Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Questions</h2>
          <p className="text-gray-600 mb-4">Export all questions to CSV or JSON format</p>
          
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => handleExportQuestions('csv')}
              disabled={exportStatus === 'exporting'}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export as CSV
            </button>
            <button
              onClick={() => handleExportQuestions('json')}
              disabled={exportStatus === 'exporting'}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export as JSON
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Export includes:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center">
                <Info className="h-4 w-4 mr-2 text-gray-400" />
                All question details
              </li>
              <li className="flex items-center">
                <Info className="h-4 w-4 mr-2 text-gray-400" />
                Status, priority, and category
              </li>
              <li className="flex items-center">
                <Info className="h-4 w-4 mr-2 text-gray-400" />
                Module associations
              </li>
              <li className="flex items-center">
                <Info className="h-4 w-4 mr-2 text-gray-400" />
                Creation and update timestamps
              </li>
            </ul>
          </div>

          {getStatusAlert(exportStatus, exportProgress, 'export')}
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Tips for bulk operations:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use the template file to ensure correct formatting</li>
              <li>CSV files should use comma separation with a header row</li>
              <li>Priority should be either "Priority" or "Normal"</li>
              <li>Status values: open, in_progress, resolved, closed</li>
              <li>Leave optional fields empty if not needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsPage;